import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  BookOpen,
  HeartHandshake,
  Pill,
  Target,
  NotebookPen,
  Download,
  ChevronRight,
  Play,
  Wind,
  Languages,
  CheckCircle2,
  Loader2,
  Video,
  Syringe,
  Map,
  Clock,
} from "lucide-react";
import EDU from "../data/educationContent.json";
import GUIDE_FR from "../data/educationGuideFr.json";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const USER_KEY = "soarlife-client-user";
const TOKEN_KEY = "soarlife-client-token";

const DISCLAIMER = EDU.guide.disclaimer;
const LAV = "#B7A6D9";

const authHeader = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function Disclaimer() {
  return (
    <p className="mt-10 pt-6 border-t border-[#BCB3A6]/30 text-xs text-[#875F45] italic leading-relaxed max-w-2xl">
      {DISCLAIMER}
    </p>
  );
}

export default function Learn() {
  const navigate = useNavigate();
  const [view, setView] = useState("home");
  const [client] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(USER_KEY) || "null");
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!client) navigate("/portal");
  }, [client, navigate]);

  const track = (item, kind) => {
    axios.post(`${API}/education/track`, { item, kind }, { headers: authHeader() }).catch(() => {});
  };

  if (!client) return null;

  return (
    <div className="min-h-screen bg-[#F8F7F3]" data-testid="learn-page">
      <header className="bg-[#115E59] text-[#FFFFF8] sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/portal" className="inline-flex items-center gap-2 text-sm text-[#FFFFF8]/80 hover:text-[#FFFFF8]" data-testid="learn-back">
            <ArrowLeft size={16} /> Patient Portal
          </Link>
          <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">
            Fertility Education Center
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-8">
        {view === "home" && <LearnHome onOpen={setView} />}
        {view === "guide" && <GuideView track={track} />}
        {view === "wellness" && <WellnessView track={track} />}
        {view === "supplements" && <SupplementsView track={track} />}
        {view === "quiz" && <QuizView track={track} />}
        {view === "journal" && <JournalView track={track} />}
        <Disclaimer />
      </main>
    </div>
  );
}

function LearnHome({ onOpen }) {
  const cards = [
    { key: "guide", icon: BookOpen, title: "Fertility Success Guide", desc: "Your 9-chapter journey booklet — what to expect, how to prepare, how to thrive. PDF included.", testid: "learn-card-guide", accent: LAV },
    { key: "wellness", icon: HeartHandshake, title: "Mind & Wellness Library", desc: "Guided audio, articles in English & French, and a breathing exercise for hard days.", testid: "learn-card-wellness", accent: "#8FB8B2" },
    { key: "supplements", icon: Pill, title: "Supplement Center", desc: "Bilingual product guides for her and for him — what each supports and who it's for.", testid: "learn-card-supplements", accent: "#D4AF37" },
    { key: "quiz", icon: Target, title: "Fertility Goals Selector", desc: "Answer one question and get your personalized supplement recommendation.", testid: "learn-card-quiz", accent: "#115E59" },
    { key: "journal", icon: NotebookPen, title: "Fertility Journal", desc: "Gratitude, reflection, and anxiety worksheets — saved privately to your account.", testid: "learn-card-journal", accent: "#875F45" },
  ];
  const existing = [
    { icon: Video, title: "Video Guide Library", desc: "Injection Guide (EN/Kreyòl), IVF Timeline, Two-Week Wait", to: "/portal" },
    { icon: Syringe, title: "Injection Training Center", desc: "Step-by-step technique video + storage library", to: "/portal" },
    { icon: Map, title: "My Fertility Roadmap", desc: "Your pathway, timelines, journey score & checklists", to: "/roadmap" },
    { icon: Clock, title: "Medication Reminders", desc: "Daily schedules and dose tracking", to: "/portal#my-medications" },
  ];
  return (
    <div data-testid="learn-home">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#875F45] font-semibold mb-2">Welcome to your library</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-light text-[#3F5043] leading-tight">
          Learn, prepare, <em className="text-[#115E59]">thrive.</em>
        </h1>
        <p className="text-[#3F5043]/70 mt-3 max-w-2xl">
          Everything your Soarlife team has built for your journey — education, wellness, supplements, and tools — in one place.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <button key={c.key} onClick={() => onOpen(c.key)} data-testid={c.testid}
              className="text-left bg-white rounded-3xl border border-[#BCB3A6]/40 p-6 hover:-translate-y-1 hover:shadow-lg transition-all">
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4" style={{ background: `${c.accent}22` }}>
                <Icon size={20} style={{ color: c.accent === LAV ? "#7C6AAE" : c.accent }} />
              </div>
              <div className="font-serif text-xl text-[#3F5043] mb-1.5">{c.title}</div>
              <div className="text-sm text-[#3F5043]/70 leading-relaxed">{c.desc}</div>
              <div className="mt-4 text-xs font-semibold text-[#115E59] inline-flex items-center gap-1">
                Open <ChevronRight size={13} />
              </div>
            </button>
          );
        })}
        {/* Portal tools card */}
        <div className="bg-[#115E59] rounded-3xl p-6 text-[#FFFFF8]">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">Also in your portal</div>
          <div className="space-y-3">
            {existing.map((e) => {
              const EIcon = e.icon;
              return (
                <Link key={e.title} to={e.to} className="flex items-start gap-3 group">
                  <EIcon size={15} className="text-[#D4AF37] mt-0.5 shrink-0" />
                  <span>
                    <span className="block text-sm font-medium group-hover:underline">{e.title}</span>
                    <span className="block text-[11px] text-[#FFFFF8]/60">{e.desc}</span>
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Success Guide (booklet reader) ---------------- */
function GuideView({ track }) {
  const [ch, setCh] = useState(null);
  const [lang, setLang] = useState("en");
  const guide = lang === "fr" ? GUIDE_FR : EDU.guide;
  const chapters = guide.chapters;

  const open = (c) => {
    setCh(c);
    track(`${lang}:${c.id}`, "chapter");
  };

  if (ch) {
    return (
      <div data-testid="guide-chapter">
        <div className="flex items-center justify-between mb-5">
          <button onClick={() => setCh(null)} className="text-xs uppercase tracking-[0.2em] text-[#875F45] font-semibold inline-flex items-center gap-1" data-testid="guide-back">
            <ArrowLeft size={13} /> {lang === "fr" ? "Tous les chapitres" : "All chapters"}
          </button>
          <LangToggle lang={lang} setLang={setLang} />
        </div>
        <h2 className="font-serif text-3xl text-[#3F5043] mb-2">{ch.title}</h2>
        <p className="text-[#875F45] italic mb-6">{ch.intro}</p>
        <div className="space-y-6">
          {ch.sections.map((s, i) => (
            <div key={i} className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-5">
              <div className="font-serif text-lg text-[#115E59] mb-1.5">{s.h}</div>
              <p className="text-sm text-[#3F5043]/85 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
        {ch.tools && (
          <div className="mt-6 bg-[#F3EFFA] border border-[#B7A6D9]/50 rounded-2xl p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C6AAE] font-semibold mb-3">
              {lang === "fr" ? "Outils inclus (en anglais)" : "Included tools"}
            </div>
            <div className="flex flex-wrap gap-2">
              {ch.tools.map((t) => (
                <a key={t} href={`/guides/${t}.pdf`} target="_blank" rel="noopener noreferrer"
                  data-testid={`guide-tool-${t}`} onClick={() => track(t, "download")}
                  className="inline-flex items-center gap-1.5 bg-white border border-[#B7A6D9]/60 text-[#5E4E8C] text-xs font-semibold px-3.5 py-2 rounded-full hover:bg-[#B7A6D9]/10">
                  <Download size={12} /> {EDU.tools[t].title}
                </a>
              ))}
            </div>
          </div>
        )}
        {lang === "fr" && (
          <p className="mt-8 text-xs text-[#875F45] italic leading-relaxed max-w-2xl">{GUIDE_FR.disclaimer}</p>
        )}
      </div>
    );
  }

  return (
    <div data-testid="guide-view">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h2 className="font-serif text-3xl text-[#3F5043]">{guide.title}</h2>
            <LangToggle lang={lang} setLang={setLang} />
          </div>
          <p className="text-[#3F5043]/70 italic">{guide.subtitle}</p>
        </div>
        <a href={lang === "fr" ? "/guides/fertility-success-guide-fr.pdf" : "/guides/fertility-success-guide.pdf"}
          target="_blank" rel="noopener noreferrer"
          data-testid="guide-download-pdf" onClick={() => track(`${lang}:success-guide`, "download")}
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-[#115E59] text-[#FFFFF8] text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#0D4A46]">
          <Download size={13} /> {lang === "fr" ? "Télécharger le PDF" : "Download full PDF"}
        </a>
      </div>
      <div className="space-y-2">
        {chapters.map((c, i) => (
          <button key={c.id} onClick={() => open(c)} data-testid={`guide-chapter-${i}`}
            className="w-full text-left bg-white rounded-2xl border border-[#BCB3A6]/40 px-5 py-4 flex items-center justify-between hover:border-[#115E59]/50 transition-colors">
            <div className="flex items-center gap-4">
              <span className="font-serif text-2xl text-[#B7A6D9]">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <div className="font-serif text-lg text-[#3F5043]">{c.title}</div>
                <div className="text-xs text-[#3F5043]/60">
                  {lang === "fr" ? `${c.sections.length} sujets` : `${c.sections.length} topics`}{c.tools ? (lang === "fr" ? " · outils inclus" : " · tools included") : ""}
                </div>
              </div>
            </div>
            <ChevronRight size={16} className="text-[#899480]" />
          </button>
        ))}
      </div>
      {lang === "fr" && (
        <p className="mt-8 text-xs text-[#875F45] italic leading-relaxed max-w-2xl">{GUIDE_FR.disclaimer}</p>
      )}
    </div>
  );
}

/* ---------------- Wellness library ---------------- */
function BreathingExercise() {
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState("in"); // in | out
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setPhase((p) => (p === "in" ? "out" : "in")), phase === "in" ? 4000 : 6000);
    return () => clearInterval(t);
  }, [running, phase]);
  return (
    <div className="bg-[#115E59] rounded-3xl p-8 text-center text-[#FFFFF8]" data-testid="breathing-exercise">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-5">Guided breathing · 4 in / 6 out</div>
      <div className="flex justify-center mb-5">
        <div
          className="w-32 h-32 rounded-full flex items-center justify-center transition-all"
          style={{
            background: "radial-gradient(circle, #D4AF37 0%, #115E59 75%)",
            transform: running ? (phase === "in" ? "scale(1.35)" : "scale(0.9)") : "scale(1)",
            transitionDuration: phase === "in" ? "4000ms" : "6000ms",
          }}
        >
          <Wind size={26} className="text-[#FFFFF8]" />
        </div>
      </div>
      <div className="font-serif italic text-2xl mb-5" data-testid="breathing-phase">
        {!running ? "Ready when you are" : phase === "in" ? "Breathe in… 2… 3… 4" : "And out… 2… 3… 4… 5… 6"}
      </div>
      <button onClick={() => setRunning(!running)} data-testid="breathing-start"
        className="rounded-full px-6 py-2.5 bg-[#D4AF37] text-[#115E59] text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#FFFFF8] transition-colors">
        {running ? "Pause" : "Begin"}
      </button>
    </div>
  );
}

function WellnessView({ track }) {
  const [lang, setLang] = useState("en");
  const [art, setArt] = useState(null);
  const audio = EDU.audio;

  const open = (a) => {
    setArt(a);
    track(a.id, "article");
  };

  if (art) {
    return (
      <div data-testid="wellness-article">
        <button onClick={() => setArt(null)} className="text-xs uppercase tracking-[0.2em] text-[#875F45] font-semibold mb-5 inline-flex items-center gap-1" data-testid="wellness-back">
          <ArrowLeft size={13} /> Library
        </button>
        <div className="flex items-center gap-3 mb-1">
          <h2 className="font-serif text-3xl text-[#3F5043]">{lang === "en" ? art.title_en : art.title_fr}</h2>
          <LangToggle lang={lang} setLang={setLang} />
        </div>
        <div className="text-xs text-[#899480] mb-5">{art.min} min read</div>
        <p className="text-[#3F5043]/85 leading-relaxed text-[15px] bg-white rounded-2xl border border-[#BCB3A6]/40 p-6">
          {lang === "en" ? art.body_en : art.body_fr}
        </p>
        {art.id === "meditation" && (
          <div className="mt-5">
            <AudioCard title={lang === "en" ? audio.meditation_en.title : audio.meditation_fr.title}
              file={lang === "en" ? audio.meditation_en.file : audio.meditation_fr.file} track={track} id={`meditation-${lang}`} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="wellness-view">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-3xl text-[#3F5043]">Mind & Wellness Library</h2>
        <LangToggle lang={lang} setLang={setLang} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <AudioCard title={lang === "en" ? audio.breathing_en.title : audio.breathing_fr.title}
          file={lang === "en" ? audio.breathing_en.file : audio.breathing_fr.file} track={track} id={`breathing-${lang}`} />
        <AudioCard title={lang === "en" ? audio.meditation_en.title : audio.meditation_fr.title}
          file={lang === "en" ? audio.meditation_en.file : audio.meditation_fr.file} track={track} id={`meditation-${lang}`} />
      </div>

      <div className="mb-6"><BreathingExercise /></div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {EDU.wellness.map((a) => (
          <button key={a.id} onClick={() => open(a)} data-testid={`wellness-article-${a.id}`}
            className="text-left bg-white rounded-2xl border border-[#BCB3A6]/40 p-5 hover:border-[#B7A6D9] transition-colors">
            <div className="font-serif text-lg text-[#3F5043] mb-1">{lang === "en" ? a.title_en : a.title_fr}</div>
            <div className="text-xs text-[#899480]">{a.min} min read</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function LangToggle({ lang, setLang }) {
  return (
    <div className="flex items-center gap-1 bg-[#EDEDEE] rounded-full p-0.5" data-testid="learn-lang-toggle">
      <Languages size={12} className="text-[#875F45] ml-2" />
      {[["en", "EN"], ["fr", "FR"]].map(([k, label]) => (
        <button key={k} onClick={() => setLang(k)} data-testid={`learn-lang-${k}`}
          className={`px-3 py-1 rounded-full text-[10px] font-bold ${lang === k ? "bg-[#115E59] text-[#FFFFF8]" : "text-[#3F5043]/60"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

function AudioCard({ title, file, track, id }) {
  return (
    <div className="bg-[#F3EFFA] border border-[#B7A6D9]/50 rounded-2xl p-5" data-testid={`audio-card-${id}`}>
      <div className="flex items-center gap-2 mb-3">
        <Play size={15} className="text-[#7C6AAE]" />
        <span className="font-serif text-lg text-[#3F5043]">{title}</span>
      </div>
      <audio controls preload="none" className="w-full h-9" src={file} data-testid={`audio-player-${id}`}
        onPlay={() => track(id, "audio")} />
    </div>
  );
}

/* ---------------- Supplement center ---------------- */
const ORDER_STATUS_CLS = {
  paid: "bg-[#AE8F78]/15 text-[#875F45]",
  processing: "bg-[#3F5043]/10 text-[#3F5043]",
  shipped: "bg-[#899480]/20 text-[#3F5043]",
  delivered: "bg-[#899480]/30 text-[#3F5043]",
  pending_payment: "bg-[#AE8F78]/10 text-[#875F45]/70",
  canceled: "bg-red-100 text-red-700",
};

function SupplementsView({ track }) {
  const [lang, setLang] = useState("en");
  const [faqOpen, setFaqOpen] = useState(-1);
  const [prices, setPrices] = useState({});
  const [cart, setCart] = useState({});
  const [cartOpen, setCartOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ship, setShip] = useState({ full_name: "", phone: "", address1: "", city: "", state: "", zip: "" });
  const [checkingOut, setCheckingOut] = useState(false);
  const [err, setErr] = useState("");

  const token = () => localStorage.getItem(TOKEN_KEY);
  const cats = [["women", lang === "en" ? "For Women" : "Pour Femmes"], ["men", lang === "en" ? "For Men" : "Pour Hommes"]];

  useEffect(() => {
    axios.get(`${API}/supplements`).then((r) => {
      const map = {};
      r.data.forEach((s) => { map[s.key] = s.amount; });
      setPrices(map);
    }).catch(() => {});
    try {
      const u = JSON.parse(localStorage.getItem("soarlife-client-user") || "null");
      if (u?.full_name) setShip((s) => ({ ...s, full_name: u.full_name }));
    } catch {}
    if (token()) {
      axios.get(`${API}/supplements/mine`, { headers: { Authorization: `Bearer ${token()}` } })
        .then((r) => setOrders(r.data || [])).catch(() => {});
    }
  }, []);

  const cartCount = Object.values(cart).reduce((a, b) => a + b, 0);
  const cartTotal = Object.entries(cart).reduce((sum, [id, q]) => sum + (prices[id] || 0) * q, 0);
  const add = (id) => {
    setCart({ ...cart, [id]: (cart[id] || 0) + 1 });
    setCartOpen(true);
  };
  const setQty = (id, q) => {
    const next = { ...cart };
    if (q <= 0) delete next[id];
    else next[id] = q;
    setCart(next);
  };

  const checkout = async (e) => {
    e.preventDefault();
    if (checkingOut) return;
    setCheckingOut(true);
    setErr("");
    try {
      const res = await axios.post(`${API}/supplements/checkout`, {
        items: Object.entries(cart).map(([id, qty]) => ({ id, qty })),
        ...ship,
        origin_url: window.location.origin,
      }, { headers: { Authorization: `Bearer ${token()}` } });
      window.location.href = res.data.checkout_url;
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Checkout failed — please try again.");
      setCheckingOut(false);
    }
  };

  const FAQS = lang === "en" ? [
    ["Do supplements replace treatment?", "No. Supplements support general reproductive wellness — they never replace your clinic's protocol. Always tell your fertility team what you're taking."],
    ["When should I start?", "Most preconception supplements work best started 1–3 months before conception or a cycle, since eggs and sperm take about that long to develop."],
    ["How do I purchase?", "Add items to your cart and check out securely right here — your Soarlife nurse still reviews every order against your plan before it ships."],
    ["Are these safe with my medications?", "Usually yes, but interactions exist. Your Soarlife nurse reviews every recommendation against your medication list before you buy."],
  ] : [
    ["Les suppléments remplacent-ils le traitement ?", "Non. Ils soutiennent le bien-être reproductif général — ils ne remplacent jamais le protocole de votre clinique."],
    ["Quand commencer ?", "Idéalement 1 à 3 mois avant la conception ou un cycle — c'est le temps que mettent ovocytes et spermatozoïdes à se développer."],
    ["Comment acheter ?", "Ajoutez au panier et payez en toute sécurité ici — votre infirmière Soarlife vérifie chaque commande avant l'envoi."],
    ["Sont-ils sûrs avec mes médicaments ?", "Généralement oui, mais des interactions existent. Votre infirmière Soarlife vérifie chaque recommandation avant l'achat."],
  ];

  const inputCls =
    "w-full bg-white border border-[#BCB3A6]/50 rounded-2xl px-4 py-2.5 text-sm text-[#3F5043] placeholder-[#3F5043]/40 focus:outline-none focus:ring-2 focus:ring-[#899480]";

  return (
    <div data-testid="supplements-view">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h2 className="font-serif text-3xl text-[#3F5043]">Soarlife Supplement Center</h2>
        <LangToggle lang={lang} setLang={setLang} />
      </div>
      <p className="text-sm text-[#3F5043]/70 mb-6 max-w-2xl">
        {lang === "en"
          ? "Educational product guidance reviewed by your nurse team. Every recommendation is checked against your treatment plan."
          : "Guides éducatifs relus par votre équipe infirmière. Chaque recommandation est vérifiée avec votre plan de traitement."}
      </p>
      {cats.map(([cat, label]) => (
        <div key={cat} className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#875F45] font-semibold mb-3">{label}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {EDU.supplements.filter((s) => s.cat === cat).map((s) => (
              <div key={s.id} data-testid={`supplement-card-${s.id}`}
                className="bg-white rounded-3xl border border-[#BCB3A6]/40 p-6 flex flex-col" onMouseEnter={() => track(s.id, "supplement")}>
                <div className="flex items-center gap-2 mb-2">
                  <Pill size={16} className="text-[#B7A6D9]" />
                  <div className="font-serif text-xl text-[#115E59]">{lang === "en" ? s.name : s.name_fr}</div>
                </div>
                <p className="text-sm text-[#3F5043]/80 italic mb-3">{lang === "en" ? s.purpose_en : s.purpose_fr}</p>
                <ul className="space-y-1.5 mb-4">
                  {(lang === "en" ? s.benefits_en : s.benefits_fr).map((b) => (
                    <li key={b} className="flex gap-2 text-sm text-[#3F5043]/80">
                      <CheckCircle2 size={14} className="text-[#899480] mt-0.5 shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
                <div className="text-xs text-[#7C6AAE] bg-[#F3EFFA] rounded-xl px-3 py-2 mb-4">
                  {lang === "en" ? "Best for: " : "Idéal pour : "}{lang === "en" ? s.bestfor_en : s.bestfor_fr}
                </div>
                <div className="mt-auto flex items-center justify-between gap-3">
                  {prices[s.id] != null && (
                    <span className="font-serif text-2xl text-[#875F45]" data-testid={`supplement-price-${s.id}`}>
                      ${(prices[s.id] / 100).toFixed(2)}
                    </span>
                  )}
                  <button
                    onClick={() => add(s.id)}
                    data-testid={`supplement-add-${s.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 bg-[#115E59] text-white text-xs font-semibold hover:bg-[#875F45] transition-colors"
                  >
                    {lang === "en" ? "Add to cart" : "Ajoute nan panye"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* My orders */}
      {orders.length > 0 && (
        <div className="mb-8" data-testid="supplement-orders-mine">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#875F45] font-semibold mb-3">
            {lang === "en" ? "My orders" : "Komand mwen yo"}
          </div>
          <div className="space-y-2">
            {orders.map((o) => (
              <div key={o.id} data-testid={`my-order-${o.id}`}
                className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-[#3F5043]">
                  {o.items.map((it) => `${it.qty}× ${it.name}`).join(" · ")}
                  <span className="text-[#3F5043]/50 text-xs ml-2">${Number(o.total).toFixed(2)}</span>
                </div>
                <span className={`text-[10px] uppercase tracking-wider font-semibold px-3 py-1 rounded-full ${ORDER_STATUS_CLS[o.status] || ""}`}>
                  {o.status === "pending_payment" ? "unpaid" : o.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4">
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#875F45] font-semibold mb-3">FAQ</div>
        <div className="space-y-2">
          {FAQS.map(([q, a], i) => (
            <div key={i} className="bg-white rounded-2xl border border-[#BCB3A6]/40 overflow-hidden">
              <button onClick={() => setFaqOpen(faqOpen === i ? -1 : i)} data-testid={`supp-faq-${i}`}
                className="w-full text-left px-5 py-4 flex items-center justify-between">
                <span className="font-serif text-base text-[#3F5043]">{q}</span>
                <ChevronRight size={15} className={`text-[#899480] transition-transform ${faqOpen === i ? "rotate-90" : ""}`} />
              </button>
              {faqOpen === i && <p className="px-5 pb-4 text-sm text-[#3F5043]/75 leading-relaxed">{a}</p>}
            </div>
          ))}
        </div>
      </div>

      {/* Floating cart pill */}
      {cartCount > 0 && !cartOpen && (
        <button
          onClick={() => setCartOpen(true)}
          data-testid="cart-open"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 inline-flex items-center gap-2 rounded-full px-6 py-3.5 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium shadow-2xl hover:bg-[#875F45] transition-colors"
        >
          {lang === "en" ? "Cart" : "Panye"} · {cartCount} · ${(cartTotal / 100).toFixed(2)}
        </button>
      )}

      {/* Cart / checkout drawer */}
      {cartOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-[#3F5043]/50 backdrop-blur-sm p-4"
          data-testid="cart-drawer" onClick={() => setCartOpen(false)}>
          <div className="w-full max-w-md bg-[#F8F7F3] rounded-3xl p-6 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <div className="font-serif italic text-2xl text-[#3F5043] mb-4">
              {lang === "en" ? "Your order" : "Komand ou"}
            </div>
            {Object.keys(cart).length === 0 && (
              <p className="text-sm text-[#3F5043]/60 mb-4">{lang === "en" ? "Your cart is empty." : "Panye w vid."}</p>
            )}
            <div className="space-y-2 mb-5">
              {Object.entries(cart).map(([id, qty]) => {
                const meta = EDU.supplements.find((s) => s.id === id);
                return (
                  <div key={id} data-testid={`cart-item-${id}`} className="flex items-center justify-between gap-3 bg-white rounded-2xl border border-[#BCB3A6]/40 px-4 py-3">
                    <div className="text-sm text-[#3F5043] flex-1">{meta?.name || id}</div>
                    <div className="flex items-center gap-2">
                      <button data-testid={`cart-minus-${id}`} onClick={() => setQty(id, qty - 1)}
                        className="w-7 h-7 rounded-full bg-[#EDEDEE] text-[#3F5043] text-sm">−</button>
                      <span className="text-sm w-5 text-center text-[#3F5043]">{qty}</span>
                      <button data-testid={`cart-plus-${id}`} onClick={() => setQty(id, qty + 1)}
                        className="w-7 h-7 rounded-full bg-[#EDEDEE] text-[#3F5043] text-sm">+</button>
                    </div>
                    <div className="text-sm font-medium text-[#875F45] w-16 text-right">
                      ${(((prices[id] || 0) * qty) / 100).toFixed(2)}
                    </div>
                  </div>
                );
              })}
            </div>

            {Object.keys(cart).length > 0 && (
              <form onSubmit={checkout} className="space-y-3" data-testid="supplement-checkout-form">
                <input required data-testid="ship-name" placeholder={lang === "en" ? "Full name" : "Non konplè"}
                  value={ship.full_name} onChange={(e) => setShip({ ...ship, full_name: e.target.value })} className={inputCls} />
                <input data-testid="ship-phone" type="tel" placeholder={lang === "en" ? "Phone (for delivery texts)" : "Telefòn"}
                  value={ship.phone} onChange={(e) => setShip({ ...ship, phone: e.target.value })} className={inputCls} />
                <input required data-testid="ship-address1" placeholder={lang === "en" ? "Street address" : "Adrès"}
                  value={ship.address1} onChange={(e) => setShip({ ...ship, address1: e.target.value })} className={inputCls} />
                <div className="grid grid-cols-3 gap-3">
                  <input required data-testid="ship-city" placeholder={lang === "en" ? "City" : "Vil"}
                    value={ship.city} onChange={(e) => setShip({ ...ship, city: e.target.value })} className={inputCls} />
                  <input required data-testid="ship-state" placeholder="ST" maxLength={2}
                    value={ship.state} onChange={(e) => setShip({ ...ship, state: e.target.value })} className={inputCls} />
                  <input required data-testid="ship-zip" placeholder="ZIP"
                    value={ship.zip} onChange={(e) => setShip({ ...ship, zip: e.target.value })} className={inputCls} />
                </div>
                <p className="text-[11px] text-[#875F45] leading-relaxed">
                  {lang === "en"
                    ? "Your Soarlife nurse reviews every order against your protocol before it ships. Supplements support wellness — they never replace treatment."
                    : "Enfimyè Soarlife w verifye chak komand ak pwotokòl ou anvan li voye. Sipò byennèt — pa ranplase tretman."}
                </p>
                {err && <p className="text-xs text-red-700" data-testid="supplement-checkout-error">{err}</p>}
                <button type="submit" disabled={checkingOut} data-testid="supplement-pay-btn"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors disabled:opacity-40">
                  {checkingOut ? <Loader2 size={15} className="animate-spin" /> : null}
                  {lang === "en" ? "Pay" : "Peye"} ${(cartTotal / 100).toFixed(2)} {lang === "en" ? "securely" : "an tout sekirite"}
                </button>
              </form>
            )}
            <button onClick={() => setCartOpen(false)} data-testid="cart-close"
              className="mt-4 w-full text-center text-xs text-[#3F5043]/60 hover:text-[#3F5043]">
              {lang === "en" ? "Continue browsing" : "Kontinye gade"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Goals quiz ---------------- */
function QuizView({ track }) {
  const [who, setWho] = useState(null);
  const [result, setResult] = useState(null);
  const quiz = EDU.goals_quiz;

  const pick = (g) => {
    setResult(g.rec);
    track(`${who}:${g.goal}`, "quiz");
  };
  const supp = result ? EDU.supplements.find((s) => s.id === result) : null;

  return (
    <div data-testid="quiz-view" className="max-w-xl mx-auto">
      <h2 className="font-serif text-3xl text-[#3F5043] text-center mb-2">What Is Your Fertility Goal?</h2>
      <p className="text-center text-sm text-[#3F5043]/70 mb-8">One question. One personalized recommendation.</p>

      {!who && (
        <div className="grid grid-cols-2 gap-4">
          {[["women", "For me (female journey)"], ["men", "For him (male journey)"]].map(([k, label]) => (
            <button key={k} onClick={() => setWho(k)} data-testid={`quiz-who-${k}`}
              className="bg-white rounded-3xl border border-[#BCB3A6]/40 p-8 text-center font-serif text-lg text-[#115E59] hover:border-[#115E59]/50 transition-colors">
              {label}
            </button>
          ))}
        </div>
      )}

      {who && !supp && (
        <div className="space-y-2">
          {quiz[who].map((g, i) => (
            <button key={g.goal} onClick={() => pick(g)} data-testid={`quiz-goal-${i}`}
              className="w-full text-left bg-white rounded-2xl border border-[#BCB3A6]/40 px-5 py-4 font-serif text-lg text-[#3F5043] hover:border-[#115E59]/50 flex items-center justify-between transition-colors">
              {g.goal} <ChevronRight size={15} className="text-[#899480]" />
            </button>
          ))}
        </div>
      )}

      {supp && (
        <div data-testid="quiz-result" className="bg-white rounded-3xl border-2 border-[#D4AF37]/60 p-8 text-center">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#875F45] font-semibold mb-2">Your recommendation</div>
          <div className="w-12 h-12 rounded-full bg-[#F3EFFA] flex items-center justify-center mx-auto mb-3">
            <Pill size={20} className="text-[#7C6AAE]" />
          </div>
          <div className="font-serif text-2xl text-[#115E59] mb-2">{supp.name}</div>
          <p className="text-sm text-[#3F5043]/75 mb-4">{supp.purpose_en}</p>
          <p className="text-xs text-[#3F5043]/55 mb-5">Ask your Soarlife care team before starting — we'll confirm it fits your plan.</p>
          <div className="flex justify-center gap-3">
            <Link to="/portal" className="rounded-full px-5 py-2.5 bg-[#115E59] text-[#FFFFF8] text-xs font-semibold uppercase tracking-[0.15em]">
              Message my nurse
            </Link>
            <button onClick={() => { setWho(null); setResult(null); }} data-testid="quiz-restart"
              className="rounded-full px-5 py-2.5 bg-[#EDEDEE] text-[#3F5043] text-xs font-semibold uppercase tracking-[0.15em]">
              Start over
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Journal ---------------- */
function JournalView({ track }) {
  const [tool, setTool] = useState(null);
  const [values, setValues] = useState({});
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  const tools = ["gratitude-journal", "reflection-journal", "anxiety-worksheet"];

  const load = async () => {
    try {
      const res = await axios.get(`${API}/journal`, { headers: authHeader() });
      setEntries(res.data || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!tool) return;
    setSaving(true);
    try {
      await axios.post(`${API}/journal`, { tool, content: values }, { headers: authHeader() });
      track(tool, "journal");
      setValues({});
      setTool(null);
      load();
    } catch {} finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="journal-view" className="max-w-2xl mx-auto">
      <h2 className="font-serif text-3xl text-[#3F5043] mb-6 text-center">My Fertility Journal</h2>

      {!tool ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
          {tools.map((t) => (
            <button key={t} onClick={() => setTool(t)} data-testid={`journal-tool-${t}`}
              className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-5 text-left hover:border-[#B7A6D9] transition-colors">
              <NotebookPen size={16} className="text-[#B7A6D9] mb-2" />
              <div className="font-serif text-base text-[#3F5043]">{EDU.tools[t].title}</div>
            </button>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-[#BCB3A6]/40 p-6 mb-8">
          <div className="font-serif text-xl text-[#115E59] mb-1">{EDU.tools[tool].title}</div>
          <p className="text-sm text-[#875F45] italic mb-4">{EDU.tools[tool].prompt}</p>
          <div className="space-y-4">
            {EDU.tools[tool].fields.map((f) => (
              <div key={f}>
                <label className="block text-xs uppercase tracking-[0.15em] text-[#899480] font-semibold mb-1.5">{f}</label>
                <textarea
                  data-testid={`journal-field-${f.slice(0, 12).replace(/\W/g, "-")}`}
                  rows={2}
                  value={values[f] || ""}
                  onChange={(e) => setValues({ ...values, [f]: e.target.value })}
                  className="w-full bg-[#F8F7F3] border border-[#BCB3A6]/50 rounded-2xl px-4 py-2.5 text-sm text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#899480] resize-none"
                />
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-5">
            <button onClick={save} disabled={saving} data-testid="journal-save"
              className="flex-1 rounded-full px-5 py-2.5 bg-[#115E59] text-[#FFFFF8] text-xs font-semibold uppercase tracking-[0.15em] disabled:opacity-60">
              {saving ? <Loader2 size={13} className="animate-spin inline" /> : "Save privately"}
            </button>
            <button onClick={() => setTool(null)} className="rounded-full px-5 py-2.5 bg-[#EDEDEE] text-[#3F5043] text-xs font-semibold uppercase tracking-[0.15em]">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="text-[11px] uppercase tracking-[0.25em] text-[#875F45] font-semibold mb-3">Past entries</div>
      <div className="space-y-2" data-testid="journal-entries">
        {entries.length === 0 && <div className="text-sm text-[#3F5043]/60 text-center py-6">Your private entries will appear here.</div>}
        {entries.map((e, i) => (
          <div key={e.id} data-testid={`journal-entry-${i}`} className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-4">
            <div className="flex justify-between text-xs text-[#899480] mb-1">
              <span className="font-semibold">{EDU.tools[e.tool]?.title || e.tool}</span>
              <span>{new Date(e.created_at).toLocaleDateString()}</span>
            </div>
            {Object.entries(e.content).filter(([, v]) => v).map(([k, v]) => (
              <div key={k} className="text-sm text-[#3F5043]/85"><span className="text-[#875F45]">{k}</span> {v}</div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
